import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import CounselorFeedbackList from '@/components/shared/CounselorFeedbackList';
import NotificationBell from '@/components/shared/NotificationBell';
import StudentAISummary from '@/components/shared/StudentAISummary';
import { useFeedback } from '@/hooks/useFeedback';
import { MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParentDashboard = ({ user, onLogout }) => {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState([]);
  const [emotionalData, setEmotionalData] = useState({ emotional_data: [], checkins: [] });
  const [loading, setLoading] = useState(false);
  
  // Reports state
  const [examReports, setExamReports] = useState([]);
  const [assignmentReports, setAssignmentReports] = useState([]);
  const [quizReports, setQuizReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  
  // AI Insights state
  const [comprehensiveEvaluation, setComprehensiveEvaluation] = useState(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const { feedback: counselorFeedback, unreadCount: feedbackUnread, markAsRead: markFeedbackRead } = useFeedback('parent');

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API}/parent/children`);
      setChildren(response.data.children || []);
      if (response.data.children && response.data.children.length > 0) {
        setSelectedChild(response.data.children[0]);
      }
    } catch (error) {
      console.error('Failed to fetch children', error);
    }
  };

  useEffect(() => {
    if (selectedChild) {
      fetchChildProgress(selectedChild.id);
      fetchChildEmotionalData(selectedChild.id);
      fetchExamReports(selectedChild.id);
      fetchAssignmentReports(selectedChild.id);
      fetchQuizReports(selectedChild.id);
      fetchAnalytics(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchChildProgress = async (childId) => {
    try {
      const response = await axios.get(`${API}/parent/child/${childId}/progress`);
      setChildProgress(response.data.progress || []);
    } catch (error) {
      console.error('Failed to fetch child progress', error);
    }
  };

  const fetchChildEmotionalData = async (childId) => {
    try {
      const response = await axios.get(`${API}/parent/child/${childId}/emotional`);
      setEmotionalData(response.data);
    } catch (error) {
      console.error('Failed to fetch emotional data', error);
    }
  };

  const fetchExamReports = async (childId) => {
    try {
      const response = await axios.get(`${API}/parent/child/${childId}/exam-reports`);
      setExamReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch exam reports', error);
    }
  };

  const fetchAssignmentReports = async (childId) => {
    try {
      const response = await axios.get(`${API}/parent/child/${childId}/assignment-reports`);
      setAssignmentReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch assignment reports', error);
    }
  };

  const fetchQuizReports = async (childId) => {
    try {
      const response = await axios.get(`${API}/parent/child/${childId}/quiz-reports`);
      setQuizReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch quiz reports', error);
    }
  };

  const fetchAnalytics = async (childId) => {
    try {
      const response = await axios.get(`${API}/parent/child/${childId}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics', error);
    }
  };

  const generateComprehensiveEvaluation = async () => {
    if (!selectedChild) return;
    
    setEvaluationLoading(true);
    try {
      const response = await axios.post(
        `${API}/parent/child/${selectedChild.id}/comprehensive-evaluation`,
        {}
      );
      setComprehensiveEvaluation(response.data);
      toast.success('Evaluation generated successfully!');
    } catch (error) {
      toast.error('Failed to generate evaluation');
    } finally {
      setEvaluationLoading(false);
    }
  };

  const handleConsentToggle = async (childId, currentStatus) => {
    setLoading(true);
    try {
      await axios.post(`${API}/parent/consent`, {
        child_id: childId,
        consent_status: !currentStatus,
        consent_type: 'data_collection'
      });
      toast.success('Consent updated successfully');
      fetchChildren();
    } catch (error) {
      toast.error('Failed to update consent');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedChild) return;
    
    try {
      toast.info('Generating PDF report...');
      const response = await axios.get(
        `${API}/parent/child/${selectedChild.id}/comprehensive-evaluation/pdf`,
        { responseType: 'blob' }
      );
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `evaluation_${selectedChild.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF report');
    }
  };

  const calculateAverageScore = () => {
    if (childProgress.length === 0) return 0;
    const total = childProgress.reduce((sum, p) => sum + p.score, 0);
    return (total / childProgress.length).toFixed(1);
  };

  const getAverageMood = () => {
    if (emotionalData.emotional_data.length === 0) return 'N/A';
    const avgSentiment = emotionalData.emotional_data.reduce((sum, e) => sum + e.sentiment_score, 0) / emotionalData.emotional_data.length;
    if (avgSentiment > 0.6) return 'Positive 😊';
    if (avgSentiment < 0.4) return 'Needs Support 😔';
    return 'Neutral 😐';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Parent Dashboard</h1>
                <p className="text-sm text-gray-600">Welcome, {user?.name}!</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button
                onClick={onLogout}
                variant="outline"
                className="border-gray-300 hover:bg-gray-100"
                data-testid="logout-button"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children.length === 0 ? (
          <Card className="p-12 text-center bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No children linked yet</h3>
            <p className="text-gray-600 mb-4">Please contact a superuser to link your child's account</p>
          </Card>
        ) : (
          <>
            {/* Child Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Child</label>
              <div className="flex gap-3 flex-wrap">
                {children.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => setSelectedChild(child)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      selectedChild?.id === child.id
                        ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-violet-300'
                    }`}
                    data-testid={`select-child-${child.id}`}
                  >
                    {child.name}
                  </button>
                ))}
              </div>
            </div>

            {selectedChild && (
              <>
                {/* AI Progress Summary */}
                <StudentAISummary studentId={selectedChild.id} studentName={selectedChild.name} />

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Average Score</p>
                        <p className="text-3xl font-bold text-violet-600">{calculateAverageScore()}%</p>
                      </div>
                      <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Quizzes</p>
                        <p className="text-3xl font-bold text-purple-600">{childProgress.length}</p>
                      </div>
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Mood Trend</p>
                        <p className="text-lg font-bold text-pink-600">{getAverageMood()}</p>
                      </div>
                      <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                        <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                    <div>
                      <p className="text-sm text-gray-600 mb-3">Data Consent</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {selectedChild.consent_status ? 'Enabled' : 'Disabled'}
                        </span>
                        <Switch
                          checked={selectedChild.consent_status}
                          onCheckedChange={() => handleConsentToggle(selectedChild.id, selectedChild.consent_status)}
                          disabled={loading}
                          data-testid="consent-toggle"
                        />
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Detailed Information */}
                <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                  <Tabs defaultValue="progress" className="w-full">
                    <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0">
                      <TabsTrigger
                        value="progress"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent"
                        data-testid="progress-tab"
                      >
                        Academic Progress
                      </TabsTrigger>
                      <TabsTrigger
                        value="emotional"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent"
                        data-testid="emotional-tab"
                      >
                        Emotional Well-being
                      </TabsTrigger>
                      <TabsTrigger
                        value="reports"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent"
                      >
                        Academic Reports
                      </TabsTrigger>
                      <TabsTrigger
                        value="ai-insights"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent"
                      >
                        AI Insights
                      </TabsTrigger>
                      <TabsTrigger
                        value="counselor-feedback"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent"
                        data-testid="parent-feedback-tab"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" />
                        Counselor {feedbackUnread > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full" data-testid="feedback-unread-badge">{feedbackUnread}</span>}
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="progress" className="p-6" data-testid="progress-content">
                      <h3 className="text-lg font-semibold mb-4">Recent Quiz Results</h3>
                      {childProgress.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <p>No quiz results yet</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {childProgress.map((item, idx) => (
                            <Card key={idx} className="p-4 border border-gray-200">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="font-semibold text-gray-900 capitalize">{item.subject}</h4>
                                  <p className="text-sm text-gray-600">
                                    {new Date(item.timestamp).toLocaleDateString()} at {new Date(item.timestamp).toLocaleTimeString()}
                                  </p>
                                </div>
                                <Badge
                                  className={`${
                                    item.score >= 80 ? 'bg-green-100 text-green-700' :
                                    item.score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {item.score.toFixed(0)}%
                                </Badge>
                              </div>
                              <Progress value={item.score} className="h-2" />
                            </Card>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="emotional" className="p-6" data-testid="emotional-content">
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Recent Check-ins</h3>
                          {emotionalData.checkins.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                              <p>No emotional check-ins yet</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {emotionalData.checkins.map((checkin, idx) => (
                                <Card key={idx} className="p-4 border border-gray-200">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <p className="font-medium capitalize">Mood: {checkin.mood}</p>
                                      <p className="text-sm text-gray-600">
                                        Energy Level: {checkin.energy_level}/10
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">
                                        {new Date(checkin.timestamp).toLocaleString()}
                                      </p>
                                    </div>
                                    <div className="text-3xl">
                                      {checkin.mood === 'happy' && '😊'}
                                      {checkin.mood === 'neutral' && '😐'}
                                      {checkin.mood === 'sad' && '😔'}
                                      {checkin.mood === 'stressed' && '😰'}
                                      {checkin.mood === 'tired' && '😴'}
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold mb-4">Sentiment Analysis</h3>
                          {emotionalData.emotional_data.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                              <p>No sentiment data available</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {emotionalData.emotional_data.slice(0, 5).map((data, idx) => (
                                <Card key={idx} className="p-4 border border-gray-200">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <p className="text-sm text-gray-700 mb-1">"{data.message}"</p>
                                      <p className="text-xs text-gray-500">
                                        {new Date(data.timestamp).toLocaleString()}
                                      </p>
                                    </div>
                                    <Badge
                                      className={`ml-3 ${
                                        data.tone === 'positive' ? 'bg-green-100 text-green-700' :
                                        data.tone === 'negative' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}
                                    >
                                      {data.tone}
                                    </Badge>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="reports" className="p-6">
                      <div className="space-y-6">
                        {/* Exam Reports */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Exam Results</h3>
                          {examReports.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No exam submissions yet</p>
                          ) : (
                            <div className="space-y-3">
                              {examReports.map((report, idx) => (
                                <Card key={idx} className="p-4 border">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <h4 className="font-semibold">{report.exam_title || 'Exam'}</h4>
                                      <p className="text-sm text-gray-600">{report.subject} - Grade: {report.grade_level}</p>
                                      <div className="mt-2">
                                        <Badge className={
                                          report.percentage >= 90 ? 'bg-green-500' :
                                          report.percentage >= 75 ? 'bg-blue-500' :
                                          report.percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                        }>
                                          {(report.percentage ?? 0).toFixed(1)}% ({report.total_score ?? 0} points)
                                        </Badge>
                                      </div>
                                      {report.teacher_feedback && (
                                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                          <div className="flex gap-2 mb-1">
                                            {report.teacher_grade && (
                                              <Badge>Teacher Grade: {report.teacher_grade}/100</Badge>
                                            )}
                                            {report.teacher_rating && (
                                              <Badge className="bg-purple-500">{report.teacher_rating}</Badge>
                                            )}
                                          </div>
                                          <p className="text-sm font-medium text-yellow-900">Teacher Feedback:</p>
                                          <p className="text-sm text-gray-700">{report.teacher_feedback}</p>
                                          <p className="text-xs text-gray-500 mt-1">
                                            {new Date(report.feedback_date).toLocaleString()}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-sm text-gray-500">
                                      {new Date(report.submitted_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Quiz Reports */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Practice Quiz Results</h3>
                          {quizReports.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No quiz submissions yet</p>
                          ) : (
                            <div className="space-y-3">
                              {quizReports.map((report, idx) => (
                                <Card key={idx} className="p-4 border">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h4 className="font-semibold capitalize">{report.subject}</h4>
                                      <p className="text-sm text-gray-600">
                                        {report.correct_answers || 0}/{report.total_questions || 0} correct
                                      </p>
                                      <Badge className={
                                        (report.score_percentage || 0) >= 80 ? 'bg-green-500' :
                                        (report.score_percentage || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                      }>
                                        {(report.score_percentage || 0).toFixed(0)}%
                                      </Badge>
                                    </div>
                                    <span className="text-sm text-gray-500">
                                      {new Date(report.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Assignment Reports */}
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Assignment Submissions</h3>
                          {assignmentReports.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No assignment submissions yet</p>
                          ) : (
                            <div className="space-y-3">
                              {assignmentReports.map((report, idx) => (
                                <Card key={idx} className="p-4 border">
                                  <div className="space-y-2">
                                    <h4 className="font-semibold">{report.assignment_title || report.title}</h4>
                                    <p className="text-sm text-gray-600">{report.subject} {report.grade_level ? `- Grade: ${report.grade_level}` : ''}</p>
                                    
                                    {/* Auto-score for question paper submissions */}
                                    {report.submission_type === 'question_paper' && (
                                      <div className="flex flex-wrap gap-2">
                                        <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">
                                          Auto: {report.earned_marks ?? 0}/{report.total_marks || 0} ({report.score_percentage ?? 0}%)
                                        </Badge>
                                        {report.ai_score_percentage !== undefined && (
                                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">
                                            AI Eval: {report.ai_score_percentage}%
                                          </Badge>
                                        )}
                                        {report.teacher_reviewed ? (
                                          <Badge className="bg-green-600 text-white text-xs">Teacher Reviewed</Badge>
                                        ) : (
                                          <Badge variant="outline" className="text-xs border-gray-300 text-gray-500">Pending Review</Badge>
                                        )}
                                      </div>
                                    )}
                                    
                                    {report.grade && (
                                      <div className="flex gap-2">
                                        <Badge>Score: {report.grade}/100</Badge>
                                        {report.rating && (
                                          <Badge className="bg-purple-500">{report.rating}</Badge>
                                        )}
                                      </div>
                                    )}
                                    {report.feedback && (
                                      <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                        <p className="text-sm font-medium text-yellow-900">Teacher Feedback:</p>
                                        <p className="text-sm text-gray-700">{report.feedback}</p>
                                        {report.feedback_date && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {new Date(report.feedback_date).toLocaleString()}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    <p className="text-xs text-gray-500">
                                      Submitted: {new Date(report.submitted_at).toLocaleString()}
                                    </p>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="ai-insights" className="p-6">
                      <div className="space-y-6">
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-semibold">Comprehensive AI Evaluation</h3>
                          <Button
                            onClick={generateComprehensiveEvaluation}
                            disabled={evaluationLoading || !selectedChild}
                          >
                            {evaluationLoading ? 'Generating...' : 'Generate Evaluation'}
                          </Button>
                        </div>

                        {comprehensiveEvaluation ? (
                          comprehensiveEvaluation.error ? (
                            <Card className="p-6 border-red-200 bg-red-50">
                              <p className="text-red-700">Error: {comprehensiveEvaluation.error}</p>
                            </Card>
                          ) : (
                            <div className="space-y-4">
                              {/* Overall Summary */}
                              <Card className="p-6 bg-gradient-to-br from-purple-50 to-blue-50">
                                <h4 className="text-xl font-bold mb-2">Overall Assessment</h4>
                                {comprehensiveEvaluation.performance_band && (
                                  <Badge className="text-lg px-4 py-1 mb-3 bg-blue-600">
                                    {comprehensiveEvaluation.performance_band}
                                  </Badge>
                                )}
                                {comprehensiveEvaluation.total_score && (
                                  <p className="text-lg font-semibold mb-2">
                                    Total Score: {comprehensiveEvaluation.total_score} / 35
                                  </p>
                                )}
                                {comprehensiveEvaluation.overall_summary && (
                                  <p className="text-gray-700">{comprehensiveEvaluation.overall_summary}</p>
                                )}
                              </Card>

                              {/* Dimension Scores */}
                              {Object.keys(comprehensiveEvaluation).filter(key => 
                                typeof comprehensiveEvaluation[key] === 'object' && 
                                comprehensiveEvaluation[key]?.score
                              ).map((dimension) => {
                                const data = comprehensiveEvaluation[dimension];
                                return (
                                  <Card key={dimension} className="p-6">
                                    <div className="flex justify-between items-start mb-3">
                                      <h4 className="font-semibold text-lg capitalize">
                                        {dimension.replace(/_/g, ' ')}
                                      </h4>
                                      <Badge className="text-lg">
                                        {data.score} / 5.0
                                      </Badge>
                                    </div>
                                    <Progress value={(data.score / 5) * 100} className="h-3 mb-4" />
                                    
                                    {data.strengths && (
                                      <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded">
                                        <p className="font-medium text-green-900 text-sm">Strengths:</p>
                                        <p className="text-sm text-gray-700">{data.strengths}</p>
                                      </div>
                                    )}
                                    
                                    {data.improvements && (
                                      <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                        <p className="font-medium text-yellow-900 text-sm">Areas for Improvement:</p>
                                        <p className="text-sm text-gray-700">{data.improvements}</p>
                                      </div>
                                    )}
                                    
                                    {data.recommendations && (
                                      <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                                        <p className="font-medium text-blue-900 text-sm">Recommendations:</p>
                                        <p className="text-sm text-gray-700">{data.recommendations}</p>
                                      </div>
                                    )}
                                  </Card>
                                );
                              })}

                              <Button
                                variant="outline"
                                className="w-full"
                                onClick={handleDownloadPDF}
                                disabled={!selectedChild}
                              >
                                Download Full Report (PDF)
                              </Button>
                            </div>
                          )
                        ) : (
                          <Card className="p-12 text-center">
                            <p className="text-gray-600">
                              Click "Generate Evaluation" to create a comprehensive AI-powered assessment
                              based on all available data including exams, quizzes, assignments, and emotional wellbeing.
                            </p>
                          </Card>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="counselor-feedback" className="p-6" data-testid="parent-feedback-content">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-violet-600" /> Counselor Feedback</h3>
                      <CounselorFeedbackList
                        feedback={counselorFeedback}
                        currentUserId={user?.id}
                        onMarkRead={markFeedbackRead}
                      />
                    </TabsContent>
                  </Tabs>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;
